import noop from 'lodash/noop';
import { useState } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';

import { MessageChannel } from 'modules/graphql';
import { StyledLeftTooltip } from 'modules/workflowNodeEditors/EditorBody.styled';
import { WORKFLOW_ACTIONS } from 'modules/workflows/constants';
import { BaseOption, NestedRadioSelect, NestedRadioSelectProps } from 'ui/selects';
import { modifySubscriptionText } from '../ModifySubscriptionEditor.constants';
import { fetchMessageChannelTypes } from '../ModifySubscriptionEditor.queries';
import { getDefaultSub, parseSub } from '../ModifySubscriptionEditor.utils';
import { SubscriptionPickerProps } from './SubscriptionPicker.types';
import { parseChannelMessageTypesForSingleSelect } from './SubscriptionPicker.utils';

const SingleSubscriptionPicker = ({
  isDisabled,
  nodeData,
  placeholder,
  hasError,
  onSelect = noop,
}: SubscriptionPickerProps) => {
  const [sub, setSub] = useState(getDefaultSub(nodeData));
  const [options, setOptions] = useState<BaseOption<string>[]>([]);
  const [categoryLabels, setCategoryLabels] = useState<NestedRadioSelectProps['categoryLabels']>(
    {},
  );

  useGraphql(fetchMessageChannelTypes, {
    variables: { search: '' },
    onCompleted: ({ channelMessageTypes = [] }: { channelMessageTypes: MessageChannel[] }) => {
      const parsedTypes = parseChannelMessageTypesForSingleSelect(channelMessageTypes);

      setOptions(parsedTypes.options);
      setCategoryLabels(parsedTypes.labels);
    },
  });

  const handleSelect = (selectedVals: string[]) => {
    setSub(selectedVals);

    const nodeClass = selectedVals[0].includes(modifySubscriptionText.type)
      ? WORKFLOW_ACTIONS.MODIFY_MESSAGE_TYPE
      : WORKFLOW_ACTIONS.MODIFY_CHANNEL_SUBSCRIPTION;

    // Pick the correct resource name from the options
    // We get the resource name differently depending on whether it's a channel or a message type
    const resourceName =
      nodeClass === WORKFLOW_ACTIONS.MODIFY_CHANNEL_SUBSCRIPTION
        ? Object.values(categoryLabels).find((c) => c.id === selectedVals[0])?.label
        : options.find((t) => t.id === selectedVals[0])?.label;

    onSelect({
      nodeClass,
      channelId: parseSub(modifySubscriptionText.channel, selectedVals),
      messageTypeId: parseSub(modifySubscriptionText.type, selectedVals),
      resource: { name: resourceName as string },
    });
  };

  return (
    <StyledLeftTooltip isEnabled={isDisabled} tooltipContent={nodeData?.resource?.name}>
      <NestedRadioSelect
        id="radio-select"
        countText={{ plural: 'types', single: 'type' }}
        options={options}
        categoryLabels={categoryLabels}
        placeholder={placeholder}
        inputContent={{ filterPlaceholder: 'Search' }}
        defaultValues={sub}
        onSelect={handleSelect}
        isError={hasError}
        isReadOnly={isDisabled}
        isFullWidth
      />
    </StyledLeftTooltip>
  );
};

export default SingleSubscriptionPicker;
